import axios from 'axios';
import { SERVER_URL } from '../App';
import { GEN_ROOT_SEGMENT, stripLangPrefix } from '../utils/buildRoutes';

// A visit ends after this long without a page load, so the same person coming
// back tomorrow — or after lunch — is a second visit rather than an eternal
// first one. 30 minutes is the web-analytics convention; it is long enough that
// reading a build and then generating one stays a single visit.
const VISIT_WINDOW_MS = 30 * 60 * 1000;
const VISIT_STAMP = 'dofgen_visit_at';
const UNMAP_FLAG_PREFIX = 'dofgen_unmap_';
// Cap-gate funnel state for this browser session: unset -> 'armed' (gate shown,
// outcome pending) -> 'done' (outcome reported). One gate per session, so a
// session that hits the cap twice still contributes a single data point.
const CAP_GATE_FLAG = 'dofgen_cap_gate';

/**
 * True when the browser is driven by automation (Selenium, Puppeteer, a
 * crawler's headless Chrome). Sent with the visit, page-view and build-view
 * beacons so the backend can book the hit as a bot (see `accounts/bots.py`):
 * a person's browser never sets `navigator.webdriver`.
 */
export function isAutomated() {
    try {
        return typeof navigator !== 'undefined' && navigator.webdriver === true;
    } catch (err) {
        return false;
    }
}

/**
 * First-party visit beacon: one ping per visit, so the admin dashboard can graph
 * visits/day and the activity feed can say "someone came by" without a row per
 * page. Cookieless, best-effort, never throws.
 *
 * Call it on every page load and route change. It pings only when the previous
 * one is older than `VISIT_WINDOW_MS`; otherwise it just pushes the window
 * forward, which is what keeps a whole session of browsing to a single event.
 *
 * The stamp lives in localStorage rather than sessionStorage on purpose: a
 * second tab is the same visit, and a reload an hour later is a new one —
 * sessionStorage gets both backwards, being per-tab and lasting the tab's life.
 * It is written before the request so a flaky network can't double-count.
 */
export function recordVisit(path) {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        const last = Number(window.localStorage.getItem(VISIT_STAMP)) || 0;
        const now = Date.now();
        window.localStorage.setItem(VISIT_STAMP, String(now));
        if (now - last < VISIT_WINDOW_MS) return;
        // The landing page as a bare path: no query string, which is where
        // anything identifying would be.
        const landing = typeof path === 'string' ? path : '';
        axios.post(`${SERVER_URL()}metrics/visit`, { path: landing, automated: isAutomated() }, { withCredentials: true }).catch(() => {});
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

function postCapGate(event) {
    axios.post(`${SERVER_URL()}metrics/cap-gate`, { event }, { withCredentials: true }).catch(() => {});
}

function capGateState() {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage.getItem(CAP_GATE_FLAG);
}

/**
 * Cap-gate outcome funnel — does hitting the guest cap convert or drive away?
 *
 * `shown` is the denominator; exactly one of `login` (clicked Discord) or
 * `stayed` (kept using the app afterwards) follows, and a session that reports
 * neither is counted as abandonment by the backend. All best-effort: a dropped
 * beacon only ever undercounts an outcome, never invents one.
 */
export function recordCapGateShown() {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        if (capGateState()) return;
        window.sessionStorage.setItem(CAP_GATE_FLAG, 'armed');
        postCapGate('shown');
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

/** Guest clicked Connect with Discord from the gate. Closes the funnel. */
export function recordCapGateLogin() {
    try {
        if (capGateState() !== 'armed') return;
        window.sessionStorage.setItem(CAP_GATE_FLAG, 'done');
        postCapGate('login');
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

/**
 * Guest did something else after the gate (navigated, or tried generating
 * again) — they didn't walk away. No-op unless a gate is awaiting an outcome.
 */
export function recordCapGateEngaged() {
    try {
        if (capGateState() !== 'armed') return;
        window.sessionStorage.setItem(CAP_GATE_FLAG, 'done');
        postCapGate('stayed');
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

// --- Browser error beacon ---------------------------------------------------
// Most of this app runs in the browser — the solver included — so a failure here
// is invisible from the server unless the browser reports it. These knobs are
// what keep "report every error" from meaning "report the same error 4000 times":
// a render loop that throws would otherwise beacon on every frame.
const ERROR_FLAG_PREFIX = 'dofgen_err_';
const ERROR_COUNT_KEY = 'dofgen_err_n';
// Per browser session. A visitor who trips more distinct errors than this is
// having a bad enough time that the first few already say so.
const MAX_ERRORS_PER_SESSION = 5;
// The backend truncates at CLIENT_ERROR_MAX_MESSAGE (120) and the column holds
// 128, so anything longer is cut there instead of here. Matched so the trimming
// happens where the location suffix can still be protected.
const MAX_ERROR_MESSAGE = 120;

// Errors thrown by browser extensions land on these handlers too. An extension's
// injected script shares the page with ours, so its uncaught throws and rejected
// promises arrive indistinguishable from our own until you look at where they
// came from — and a crypto wallet mid-onboarding is not a dofgen bug anybody can
// fix from here. Two tests, because extension code reaches us two ways.
const EXTENSION_URL_SCHEMES = [
    'chrome-extension://',
    'moz-extension://',
    'safari-web-extension://',
    'safari-extension://',
    'ms-browser-extension://',
    'webkit-masked-url://',
];
// For rejections there is often no stack to read the scheme from, so a few
// messages are matched directly. Deliberately narrow: every pattern added here
// is a real error this app can no longer report.
const EXTENSION_MESSAGE_PATTERNS = [
    /\bextension\b[^.]*\b(has not been configured|context invalidated|not found)\b/i,
    /^Talisman\b/i,
];

// Globals that extensions and mobile browsers inject *into the page*, which is
// why the scheme test above never sees them: the script runs in our context, so
// the throw reports our own URL as its file. Every one of these names belongs to
// somebody else's code — Firefox/Chrome for iOS internals and wallet providers —
// and dofgen never touches any of them, so a message that references one is
// theirs by construction. Filled in from the 2026-08-28 journal, where six of
// eight entries were `__firefox__` and `window.ethereum`.
const INJECTED_GLOBALS = '__firefox__|__gCrWeb|ethereum|web3|solana|tronWeb|tronLink|keplr';
const INJECTED_GLOBAL_PATTERNS = [
    // `undefined is not an object (evaluating 'window.ethereum.selectedAddress')`
    new RegExp(`\\bwindow\\.(?:${INJECTED_GLOBALS})\\b`),
    // WebKit's and V8's two phrasings of the same missing-global ReferenceError.
    new RegExp(`[Cc]an't find variable:\\s*(?:${INJECTED_GLOBALS})\\b`),
    new RegExp(`\\b(?:${INJECTED_GLOBALS}) is not defined\\b`),
];

// How the browsers word "the request never got a response" — axios first, then
// the fetch phrasings behind it. A status code means the server answered, so
// none of those land here.
const NETWORK_FAILURE_PATTERN = /^(Network Error|Failed to fetch|Load failed|Request aborted|NetworkError\b|timeout of \d+ms exceeded)/i;

// Hosts that are somebody's development machine rather than the deployed site.
// Loopback, the `.localhost` TLD, and the three private IPv4 ranges — the last
// because testing the mobile layout means loading the dev server from a phone
// on the LAN.
const DEV_HOSTNAME_PATTERNS = [
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)$/i,
    /\.localhost$/i,
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/,
    /^192\.168\.\d{1,3}\.\d{1,3}$/,
    /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/,
];

/** True for failures raised by someone else's script rather than by this app. */
function isForeignScriptNoise(message, origin) {
    const text = String(message || '');
    const where = `${origin || ''}`;
    if (EXTENSION_URL_SCHEMES.some((scheme) => where.includes(scheme) || text.includes(scheme))) {
        return true;
    }
    if (INJECTED_GLOBAL_PATTERNS.some((pattern) => pattern.test(text))) return true;
    return EXTENSION_MESSAGE_PATTERNS.some((pattern) => pattern.test(text));
}

/** Collapse the varying parts of a message so repeats dedupe as one error. */
function errorShape(message) {
    return String(message || '').replace(/[0-9a-f]{8,}|\d+/gi, '#').slice(0, 80);
}

/**
 * True for a network failure the client's own situation already explains.
 *
 * A tab going offline or being navigated away from aborts whatever was in
 * flight, and the app dutifully reports the wreckage — which is how the journal
 * ended up with bare `Network Error` rows that name no cause and admit no fix.
 * Dropping these costs nothing: a backend that is genuinely unreachable still
 * gets reported by every visitor who is online and looking at the page.
 */
function isExplainedNetworkFailure(message) {
    if (!NETWORK_FAILURE_PATTERN.test(String(message || ''))) return false;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * True when this page is a development server rather than the deployed site.
 *
 * `npm start` points at the hosted API by design, so every error thrown on a
 * developer's laptop books as a production browser error — and hot module
 * replacement manufactures them by the handful: a variable that existed for the
 * two minutes between two edits stays live in a stale closure and throws
 * `x is not defined` from a `hot-update.js` nobody will ever load again. The
 * 2026-09-05 journal was sixteen consecutive rows of that, against four names
 * that no longer exist in the tree.
 *
 * Hostname rather than `NODE_ENV`: a production bundle served locally — a smoke
 * check against the hosted API — is still a developer, not a visitor. Nobody
 * reaches dofgen over loopback or a LAN address, so this drops no real report.
 */
function isDevOrigin() {
    if (typeof window === 'undefined') return false;
    const host = String(window.location?.hostname || '');
    return DEV_HOSTNAME_PATTERNS.some((pattern) => pattern.test(host));
}

/**
 * Append `suffix` to a message, trimming the message rather than the suffix so
 * the part that locates the failure survives the length cap.
 */
function withSuffix(message, suffix) {
    if (!suffix) return message;
    const room = Math.max(0, MAX_ERROR_MESSAGE - suffix.length);
    return `${String(message).slice(0, room)}${suffix}`;
}

/**
 * `(POST /metrics/visit)` for a failed axios call.
 *
 * Without it every dropped request reports the same three words — "Network
 * Error" — with nothing to say which call dropped, and the journal row is
 * unactionable however real the failure was. Path only: the host is ours.
 */
function requestSuffix(error) {
    const url = error?.config?.url;
    if (!url) return '';
    const method = String(error.config.method || 'get').toUpperCase();
    let path = url;
    try {
        path = new URL(url, window.location.origin).pathname;
    } catch (err) {
        // Not parseable as a URL — report whatever was passed as the path.
    }
    return ` (${method} ${path})`;
}

/**
 * Append `(file:line:col)` to a message, trimming the message so the location
 * survives the length cap rather than the other way round.
 *
 * The column is what makes the rest usable: prod ships one minified bundle, so
 * every throw in it reports line 2 and only the column tells the source map
 * which call site threw. Without it a report says a crash happened, not where —
 * which is exactly how much the 2026-08-25 tooltip crash said.
 */
function withLocation(message, event) {
    const file = event?.filename;
    if (!file) return message;
    return withSuffix(message, ` (${file}:${event.lineno || 0}:${event.colno || 0})`);
}

/**
 * Report a browser-side failure to the admin dashboard.
 *
 * Best-effort and self-limiting: deduped per (kind, message shape) and hard
 * capped per session, so a loop reports once. `kind` must be one of the slugs
 * the backend allowlists (`CLIENT_ERROR_KINDS`) or it books as "other".
 *
 * Reports from a development server, and network failures that the client being
 * offline or backgrounded already explains, are dropped here — for every caller
 * at once, which is the point of doing it at the choke point rather than in the
 * two global handlers.
 */
export function recordClientError(kind, message) {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        if (isDevOrigin()) return;
        if (isExplainedNetworkFailure(message)) return;
        const text = String(message || '').slice(0, MAX_ERROR_MESSAGE);
        const key = `${ERROR_FLAG_PREFIX}${kind}:${errorShape(text)}`;
        if (window.sessionStorage.getItem(key)) return;
        const sent = Number(window.sessionStorage.getItem(ERROR_COUNT_KEY) || 0);
        if (sent >= MAX_ERRORS_PER_SESSION) return;
        // Both written before the request: a failing beacon must not become a
        // beacon that retries forever.
        window.sessionStorage.setItem(key, '1');
        window.sessionStorage.setItem(ERROR_COUNT_KEY, String(sent + 1));
        axios
            .post(`${SERVER_URL()}metrics/client-error`, { kind, message: text }, { withCredentials: true })
            .catch(() => {});
    } catch (err) {
        // Reporting an error must never raise one.
    }
}

/**
 * Catch what no `try` around a call site can: uncaught exceptions and rejected
 * promises nobody handled. Installed once, from App.
 */
export function installGlobalErrorReporting() {
    try {
        if (typeof window === 'undefined' || window.__dofgenErrorHooked) return;
        window.__dofgenErrorHooked = true;
        window.addEventListener('error', (event) => {
            // Failed <img>/<script> loads surface here too, with no message and
            // no error object. They are not exceptions and would drown the real
            // ones, so only genuine throws are reported.
            if (!event?.error && !event?.message) return;
            const message = String(event?.message || event?.error);
            // A cross-origin script reports as a bare "Script error." with no
            // file, line, or stack. Our bundle is same-origin, so an opaque one
            // is never ours, and there is nothing in it to act on either way.
            if (!event?.filename && message.trim() === 'Script error.') return;
            if (isForeignScriptNoise(message, `${event?.filename || ''} ${event?.error?.stack || ''}`)) return;
            recordClientError('uncaught', withLocation(message, event));
        });
        window.addEventListener('unhandledrejection', (event) => {
            const reason = event?.reason;
            const message = reason?.message || String(reason || 'unhandled rejection');
            if (isForeignScriptNoise(message, reason?.stack)) return;
            // A rejected axios call has no filename to locate it, but it does
            // know which request it was.
            recordClientError('promise', withSuffix(message, requestSuffix(reason)));
        });
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

// --- Page views ---------------------------------------------------------------
// Route prefix (language stripped) -> the page name the backend counts under.
// Must stay a subset of `PAGE_VIEW_PAGES` in backend/accounts/models.py: the
// endpoint folds any name it does not know into "other".
const PAGE_BY_FIRST_SEGMENT = {
    '': 'home',
    s: 'share',
    encyclopedia: 'encyclopedia',
    'my-builds': 'my_builds',
    profile: 'profile',
    chat: 'chat',
    terms: 'terms',
};
// Routes that only redirect somewhere else. The page they land on is counted,
// so counting these too would book one load twice.
const REDIRECT_SEGMENTS = new Set(['generator']);

/**
 * The page a pathname belongs to, or null for a redirect-only route.
 * '/fr/gen/damage' -> 'gen', '/fr/gen/jack07/AbC12' -> 'gen_build'.
 */
export function pageSlug(pathname) {
    const parts = stripLangPrefix(pathname).split('/').filter(Boolean);
    const [first = '', second] = parts;
    if (REDIRECT_SEGMENTS.has(first)) return null;
    // /gen and /gen/<tab> are the scratch generator; two more segments name a build.
    if (first === GEN_ROOT_SEGMENT) return parts.length >= 3 ? 'gen_build' : 'gen';
    if (first === 'community') {
        if (!second) return 'community';
        return second === 'creators' ? 'community_creators' : 'community_creator';
    }
    if (first === 'ai' && second === 'claim') return 'ai_claim';
    return PAGE_BY_FIRST_SEGMENT[first] || 'other';
}

let lastPageViewed = null;

/**
 * Page-view beacon: one ping per page load, and per route change that lands on
 * a different page. Moving within a page — a generator tab, one build to the
 * next — is not a new page view. Development servers are not counted, for the
 * same reason they do not report errors (see `isDevOrigin`).
 */
export function recordPageView(pathname) {
    try {
        const page = pageSlug(pathname);
        if (!page || page === lastPageViewed) return;
        lastPageViewed = page;
        if (isDevOrigin()) return;
        axios.post(`${SERVER_URL()}metrics/page-view`, { page, automated: isAutomated() }, { withCredentials: true }).catch(() => {});
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}

/** Report hat/cape item ids that lack render_skin_id (once per id per session). */
export function recordUnmappedRenderSkins(itemIds) {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        const ids = [...new Set(
            (Array.isArray(itemIds) ? itemIds : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0),
        )].filter((id) => {
            const key = `${UNMAP_FLAG_PREFIX}${id}`;
            if (window.sessionStorage.getItem(key)) return false;
            window.sessionStorage.setItem(key, '1');
            return true;
        });
        if (!ids.length) return;
        axios
            .post(`${SERVER_URL()}metrics/unmapped-skin`, { item_ids: ids }, { withCredentials: true })
            .catch(() => {});
    } catch (err) {
        // Analytics must never disrupt the app.
    }
}
